import { FC } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { Modal } from 'antd';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { routes } from '@/shared/helpers';
import { Title } from '@/shared/uikit/Title';

import { useModalsStore } from '../../modals.store';
import { SmartImportSuccessModalProps } from '../../types';

export const SmartImportSuccessModal: FC<SmartImportSuccessModalProps> = ({
  total,
  failed,
  done,
  assets,
  personalIds,
  personalDocuments,
  contacts,
  onClose,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'smartImportSuccess' });
  const modals = useModalsStore();

  const handlerClose = () => {
    modals.closeModal();
    onClose();
  };

  return (
    <StyledModal
      open={true}
      footer={null}
      width={600}
      title={
        <TitleContainer>
          <Title size={22} bold>
            {t('title')}
          </Title>
        </TitleContainer>
      }
      onCancel={handlerClose}
    >
      <BodyTitle>{t('subtitle')}</BodyTitle>
      <Rows>
        <Row>
          <RowTitle>{t('total')}</RowTitle>
          <RowItem>{total}</RowItem>
        </Row>
        <Row>
          <RowTitle>{t('done')}</RowTitle>
          <RowItem>{done}</RowItem>
        </Row>
        <Row>
          <RowTitle>{t('failed')}</RowTitle>
          <RowItem>{failed}</RowItem>
        </Row>
        <Row>
          <RowTitle>{t('assets.text')}</RowTitle>
          <RowItem>
            <RowValue>{assets}</RowValue>
            <StyledLink onClick={handlerClose} href="/assets">
              {t('assets.button')}
            </StyledLink>
          </RowItem>
        </Row>
        <Row>
          <RowTitle>{t('personalIds.text')}</RowTitle>
          <RowItem>
            <RowValue>{personalIds}</RowValue>
            <StyledLink onClick={modals.closeModal} href={routes.personalIds()}>
              {t('personalIds.button')}
            </StyledLink>
          </RowItem>
        </Row>
        <Row>
          <RowTitle>{t('personalDocuments.text')}</RowTitle>
          <RowItem>
            <RowValue>{personalDocuments}</RowValue>
            <StyledLink onClick={handlerClose} href={routes.privateDocuments()}>
              {t('personalDocuments.button')}
            </StyledLink>
          </RowItem>
        </Row>
        <Row>
          <RowTitle>{t('contacts.text')}</RowTitle>
          <RowItem>
            <RowValue>{contacts}</RowValue>
            <StyledLink onClick={handlerClose} href="/contacts">
              {t('contacts.button')}
            </StyledLink>
          </RowItem>
        </Row>
      </Rows>
    </StyledModal>
  );
};

const TitleContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${COLORS.systemDark100};
`;

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0;
    }

    .ant-modal-body {
      padding: 24px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }
`;

const BodyTitle = styled.p`
  font-weight: 700;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  line-height: 20px;
`;

const RowTitle = styled.p`
  color: ${COLORS.systemDark200};
  flex: 1;
  max-width: 250px;
`;

const RowItem = styled.p`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RowValue = styled.span`
  min-width: 32px;
`;

const StyledLink = styled(Link)`
  &&& {
    color: ${COLORS.systemSky200};

    &:hover {
      color: ${COLORS.systemSky300};
    }
  }
`;
